import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { RecurringJournal } from '../../interfaces/recurringJournal.interface';
import { RecurringJournalService } from '../../services/recurring-journal.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { describeSchedule, statusColor } from './recurringJournalText';

const th = { padding: '12px', textAlign: 'left' as const, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
const td = { padding: '12px', borderBottom: '1px solid #ddd', verticalAlign: 'top' as const };

const RecurringJournalList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.JOURNAL_ENTRY_MANAGE);
    const [rows, setRows] = useState<RecurringJournal[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            setRows(await RecurringJournalService.list(institutionId));
        } catch (err) {
            toast.showApiError(err, 'Failed to load recurring journals');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const failedCount = rows.filter(r => r.hasFailedRuns).length;

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.recurringList.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Recurring journals</h2>
                {canManage && (
                    <Button data-help="accounting.recurringList.new" variant="contained" onClick={() => navigate('/accounting/recurring-journals/new')}>
                        New recurring journal
                    </Button>
                )}
            </div>
            <p style={{ color: '#555', marginTop: 0 }}>
                Templates that create a journal entry on a schedule (rent, depreciation-like accruals, subscriptions).
                The daily job runs early morning Bangladesh time; <em>Draft</em> templates leave a draft for someone to post,
                <em> Post</em> templates post automatically.
            </p>
            {failedCount > 0 && (
                <Alert data-help="accounting.recurringList.failed" severity="error" sx={{ mb: 2 }}>
                    {failedCount} template(s) have failed occurrences (for example a closed period). Open them to see the reason and retry.
                </Alert>
            )}
            {loading ? <div>Loading...</div> : rows.length === 0 ? (
                <div>No recurring journals yet.</div>
            ) : (
                <div data-help-section="accounting.recurringList.table">
                <ScrollableTable>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                        <thead>
                            <tr>
                                <th data-help="accounting.recurringList.col.name" style={th}>Name</th>
                                <th data-help="accounting.recurringList.col.schedule" style={th}>Schedule</th>
                                <th data-help="accounting.recurringList.col.nextRun" style={th}>Next run</th>
                                <th data-help="accounting.recurringList.col.amount" style={th}>Amount</th>
                                <th data-help="accounting.recurringList.col.mode" style={th}>Mode</th>
                                <th data-help="accounting.recurringList.col.status" style={th}>Status</th>
                                <th data-help="accounting.recurringList.col.lastResult" style={th}>Last result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/accounting/recurring-journals/${r.id}`)}>
                                    <td style={td}><strong>{r.name}</strong></td>
                                    <td style={td}>{describeSchedule(r)}</td>
                                    <td style={td}>{r.nextRunDate ? formatDate(r.nextRunDate) : '-'}</td>
                                    <td style={td}>{formatMoney(r.totalAmount)}</td>
                                    <td style={td}>{r.mode === 'POST' ? 'Auto-post' : 'Draft'}</td>
                                    <td style={td}>
                                        <Chip data-help="accounting.recurringList.status" size="small" label={humanizeEnum(r.status)} color={statusColor(r.status)} />
                                    </td>
                                    <td style={td}>
                                        {r.lastResult ? (
                                            <span style={{ color: r.lastResult.startsWith('Failed') ? '#c62828' : undefined }}>{r.lastResult}</span>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ScrollableTable>
                </div>
            )}
        </div>
    );
};

export default RecurringJournalList;
